import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Timestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import "../styles/List.css";

const calculateDaysWorked = (tarihMap = {}) => {
  const today = new Date();
  let totalDays = 0;

  for (let i = 1; i <= 9; i++) {
    const girisStr = tarihMap[`giris${i}`];
    const cikisStr = tarihMap[`cikis${i}`];

    if (girisStr) {
      const [girisGun, girisAy, girisYil] = String(girisStr).split("-").map(Number);
      const girisDate = new Date(girisYil, girisAy - 1, girisGun);

      let cikisDate = today;
      if (cikisStr) {
        const [cikisGun, cikisAy, cikisYil] = String(cikisStr).split("-").map(Number);
        cikisDate = new Date(cikisYil, cikisAy - 1, cikisGun);
      }

      const diffTime = cikisDate - girisDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 0) totalDays += diffDays;
    }
  }

  return totalDays;
};

const parseDateInput = (dateStr, endOfDay = false) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day, 0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toTimestamp = (dateStr, endOfDay = false) => {
  const date = parseDateInput(dateStr, endOfDay);
  return date ? Timestamp.fromDate(date) : null;
};

const formatDateTR = (date) => {
  if (!date) return "";
  return date.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const normalizeText = (value) =>
  String(value ?? "").toLocaleLowerCase("tr-TR").trim();

const getSortValue = (row, key) => {
  if (key === "tarih") return row.tarihMs || 0;
  if (key === "calismaGun") return Number(row.calismaGun) || 0;
  return normalizeText(row[key]);
};

const COLUMNS = {
  tarih: "Tarih",
  saat: "Saat",
  plaka: "Plaka",
  personel: "Personel",
  tesis: "Tesis",
  kazaBasligi: "Kaza Başlığı",
  aciklama: "Açıklama",
  calismaGun: "Çalıştığı Gün",
  kazaFotoURL: "Fotoğraf",
};

const KazalarListesi = () => {
  const [kazalar, setKazalar] = useState([]);
  const [filters, setFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0, width: 220 });
  const [sortConfig, setSortConfig] = useState({ field: "tarih", direction: "desc" });
  const filterRef = useRef(null);

  const startOfYear = new Date(new Date().getFullYear(), 0, 1);
  const today = new Date();

  const [dateRange, setDateRange] = useState({
    start: startOfYear.toISOString().split("T")[0],
    end: today.toISOString().split("T")[0],
  });

  useEffect(() => {
    const onDown = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setOpenFilter(null);
        setFilterSearch("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const startTs = toTimestamp(dateRange.start);
      const endTs = toTimestamp(dateRange.end, true);
      if (!startTs || !endTs) return;

      const kazaQuery = query(
        collection(db, "kazalar"),
        where("tarih", ">=", startTs),
        where("tarih", "<=", endTs),
        orderBy("tarih", "desc")
      );

      const kazaSnap = await getDocs(kazaQuery);
      const kazaList = kazaSnap.docs.map((docSnap) => {
        const data = docSnap.data();
        const tarihDate = data.tarih?.toDate ? data.tarih.toDate() : null;
        return {
          id: docSnap.id,
          ...data,
          tarih: formatDateTR(tarihDate),
          tarihMs: tarihDate?.getTime() || 0,
        };
      });

      const personelSnap = await getDocs(collection(db, "personeller"));
      const personeller = personelSnap.docs.map((docSnap) => ({ ...docSnap.data() }));

      const enriched = kazaList.map((kaza) => {
        const personelData = personeller.find((p) => `${p.ad} ${p.soyad}` === kaza.personel);
        return {
          ...kaza,
          tesis: personelData?.tesis || "",
          calismaGun: personelData?.tarih ? calculateDaysWorked(personelData.tarih) : "",
        };
      });

      setKazalar(enriched);
    };

    fetchData();
  }, [dateRange]);

  const filteredKazalar = useMemo(() => {
    const activeFilters = Object.entries(filters).filter(([, values]) => Array.isArray(values));
    const filtered = kazalar.filter((kaza) =>
      activeFilters.every(([key, values]) =>
        values.includes(String(kaza[key] ?? ""))
      )
    );

    return [...filtered].sort((a, b) => {
      const av = getSortValue(a, sortConfig.field);
      const bv = getSortValue(b, sortConfig.field);

      if (typeof av === "number" && typeof bv === "number") {
        return sortConfig.direction === "asc" ? av - bv : bv - av;
      }

      return sortConfig.direction === "asc"
        ? String(av).localeCompare(String(bv), "tr-TR", { sensitivity: "base" })
        : String(bv).localeCompare(String(av), "tr-TR", { sensitivity: "base" });
    });
  }, [kazalar, filters, sortConfig]);

  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (prev.field !== field) return { field, direction: "asc" };
      return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
    });
  };

  const isColumnFiltered = (col) => Array.isArray(filters[col]);

  const FilterArrow = ({ active, onClick }) => (
    <span
      onClick={onClick}
      style={{ marginLeft: 4, cursor: "pointer", color: active ? "#2563eb" : "#475569" }}
    >
      ▾
    </span>
  );

  const renderFilterDropdown = (col) => {
    const allValues = Array.from(new Set(kazalar.map((r) => String(r[col] ?? ""))))
      .sort((a, b) => String(a).localeCompare(String(b), "tr-TR", { sensitivity: "base" }));
    const values = allValues.filter((value) => normalizeText(value).includes(normalizeText(filterSearch)));
    const selected = Array.isArray(filters[col]) ? filters[col] : allValues;
    const isAllSelected = values.length > 0 && values.every((value) => selected.includes(value));

    return ReactDOM.createPortal(
      <div
        ref={filterRef}
        style={{
          position: "fixed",
          top: filterPos.top,
          left: filterPos.left,
          width: filterPos.width,
          background: "#fff",
          border: "1px solid #93c5fd",
          padding: 8,
          zIndex: 9999,
          borderRadius: 4,
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
        }}
      >
        <button
          onClick={() => handleSort(col)}
          style={{
            width: "100%",
            padding: 6,
            marginBottom: 8,
            cursor: "pointer",
            fontWeight: "bold",
            background: "#f1f5f9",
            border: "1px solid #cbd5e1",
            borderRadius: 4,
          }}
        >
          {sortConfig.field === col && sortConfig.direction === "desc"
            ? "↓ Z'den A'ya Sırala"
            : "↑ A'dan Z'ye Sırala"}
        </button>

        <input
          placeholder="Ara..."
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          style={{ width: "100%", marginBottom: 8, padding: 4, boxSizing: "border-box" }}
        />

        <label style={{ display: "flex", alignItems: "center", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #eee", fontWeight: "bold", fontSize: 12, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => {
              if (isAllSelected) {
                setFilters((prev) => ({ ...prev, [col]: allValues.filter((value) => !values.includes(value)) }));
              } else {
                const next = Array.from(new Set([...selected, ...values]));
                setFilters((prev) => ({ ...prev, [col]: next.length === allValues.length ? undefined : next }));
              }
            }}
          />
          <span style={{ marginLeft: 6 }}>Tümünü Seç / Kaldır</span>
        </label>

        <div className="ml-filter-options">
          {values.map((opt) => (
            <label key={opt} style={{ display: "flex", alignItems: "center", marginBottom: 4, fontSize: 12, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => {
                  const next = selected.includes(opt)
                    ? selected.filter((value) => value !== opt)
                    : [...selected, opt];
                  setFilters((prev) => ({ ...prev, [col]: next.length === allValues.length ? undefined : next }));
                }}
              />
              <span style={{ marginLeft: 6 }}>{opt || "(boş)"}</span>
            </label>
          ))}
        </div>
        <button onClick={() => setFilters((prev) => ({ ...prev, [col]: undefined }))} style={{ width: "100%", marginTop: 8 }}>
          Temizle
        </button>
      </div>,
      document.body
    );
  };

  const exportToExcel = () => {
    const exportData = filteredKazalar.map((kaza) => ({
      Tarih: kaza.tarih,
      Saat: kaza.saat,
      Plaka: kaza.plaka,
      Personel: kaza.personel,
      Tesis: kaza.tesis,
      "Kaza Başlığı": kaza.kazaBasligi,
      Açıklama: kaza.aciklama,
      "Çalıştığı Gün": kaza.calismaGun,
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, "Kazalar");
    XLSX.writeFile(wb, "kaza_listesi.xlsx");
  };

  const columns = Object.keys(COLUMNS);

  return (
    <div className="list-container">
      <h2>Kaza Listesi</h2>
      <style>{`
        .ml-filter-options{ max-height:180px; overflow-y:auto; border:1px solid #e5e7eb; border-radius:6px; padding:6px; background:#f8fafc; }
      `}</style>
      <div className="filter-panel">
        <label>Başlangıç Tarihi:</label>
        <input
          type="date"
          value={dateRange.start}
          onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
        />
        <label>Bitiş Tarihi:</label>
        <input
          type="date"
          value={dateRange.end}
          onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
        />
        <div style={{ marginLeft: 20, fontWeight: "bold" }}>
          {filteredKazalar.length} kaza listelendi
        </div>
        <button onClick={exportToExcel}>Excel'e Aktar</button>
      </div>
      <table>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                style={{
                  color: isColumnFiltered(col) ? "#1d4ed8" : "inherit",
                  backgroundColor: isColumnFiltered(col) ? "#eff6ff" : "#f3f4f6",
                }}
              >
                {COLUMNS[col]}
                {col !== "kazaFotoURL" && (
                  <FilterArrow
                    active={isColumnFiltered(col)}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setFilterPos({ top: rect.bottom + 4, left: rect.left, width: 220 });
                      setOpenFilter(col);
                    }}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredKazalar.map((kaza) => (
            <tr key={kaza.id}>
              <td>{kaza.tarih}</td>
              <td>{kaza.saat}</td>
              <td>{kaza.plaka}</td>
              <td>{kaza.personel}</td>
              <td>{kaza.tesis}</td>
              <td>{kaza.kazaBasligi}</td>
              <td>{kaza.aciklama}</td>
              <td>{kaza.calismaGun}</td>
              <td>
                {kaza.kazaFotoURL?.length > 0 ? (
                  <img
                    src={kaza.kazaFotoURL[0]}
                    alt="kaza"
                    style={{ width: 50, height: 50, objectFit: "cover", cursor: "pointer" }}
                    onClick={() => window.open(`/kaza-takip/kaza-detay/${kaza.id}`, "_blank")}
                  />
                ) : (
                  "-"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {openFilter && renderFilterDropdown(openFilter)}
    </div>
  );
};

export default KazalarListesi;
